import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { CurrentOrganizationService } from "@onsip/common/services/api/apiParams/current-organization.service";
import { combineLatest, Observable } from "rxjs";
import { filter, map, take } from "rxjs/operators";
import { AccountService } from "../../../../common/services/api/resources/account/account.service";
import { E911Service } from "../../../../common/services/api/resources/e911/e911.service";
import { OrganizationService } from "../../../../common/services/api/resources/organization/organization.service";
import { PackageService } from "../../../../common/services/api/resources/package/package.service";
import { views } from "../../../app/phone/views";

@Injectable({ providedIn: "root" })
export class E911Guard {
  /** credit limit for E911 provisioning if account does not have a credit card. Taken from backend code. */
  private balanceRequirement = 100;
  constructor(
    private accountService: AccountService,
    private orgService: OrganizationService,
    private e911Service: E911Service,
    private packageService: PackageService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.e911Service.orgState.pipe(take(1)).subscribe(e911 => {
      if (e911.length === 0) {
        this.e911Service.e911LocationBrowse();
      }
    });
    return combineLatest({
      org: this.orgService.state.pipe(filter(state => !state.loading)),
      accounts: this.accountService.accountState,
      e911: this.e911Service.orgState,
      packages: this.packageService.getAssignedPrimaryPackages(),
      currOrgId: CurrentOrganizationService.currentOrgId
    }).pipe(
      map(({ org, accounts, e911, packages, currOrgId }) => {
        const account = accounts[0];
        if (org.errors.length) {
          console.warn(org.errors[0]);
          this.orgService.clearErrors();
          this.router.navigate([views.HOME]);
          return false;
        }

        // super user can enable "invalid" account to have e911 through its org E911Provisioning flag so check for this flag first
        if (org.state[currOrgId].e911Provisioning) {
          if (e911.length > 0) {
            if (state.url === views.E911_TABLE) return true;
            this.router.navigate([views.E911_TABLE]);
            return false;
          } else {
            if (state.url === views.E911_NO_LOCATION) return true;
            this.router.navigate([views.E911_NO_LOCATION]);
            return false;
          }
        } else {
          // redirect to free trial empty state page if account is on free trial
          if (account.noPrepaidCredit && !packages.length) {
            if (state.url === views.E911_NO_FREE_TRIAL) return true;
            this.router.navigate([views.E911_NO_FREE_TRIAL]);
            return false;
          }
          // redirect to invoiced prepaid empty state page if account is an invoice account
          if (account.invoicedPrepaid) {
            if (state.url === views.E911_INVOICED_PREPAID) return true;
            this.router.navigate([views.E911_INVOICED_PREPAID]);
            return false;
          }
          // We are enabling the org's E911Provisioning to true if they met the right criteria.
          // we check again for api's result in the resolver to make sure there are no errors

          // these are the requirements for E911 provisioning:
          // 1. Have a credit card on file
          // 2. Have no negative prepaid PSTN credit balance
          // 3. Have no outstanding balance due
          if (
            account.creditCard &&
            parseFloat(account.balance) >= 0 &&
            parseFloat(account.receivable) <= 0
          ) {
            this.orgService.organizationEdit911Provisioning({ Enabled: true, Cascade: true });
            if (state.url === views.E911_TABLE) return true;
            this.router.navigate([views.E911_TABLE]);
            return false;
          } else if (
            // The org CAN have E911 provisioning enabled if they do not have a credit card.
            // However, the org must have a prepaid credit balance over the balance requirement aka $100
            // and an invoice balance(receivable) over the balance requirement
            !account.creditCard &&
            parseFloat(account.balance) >= this.balanceRequirement &&
            parseFloat(account.receivable) <= -1 * this.balanceRequirement
          ) {
            this.orgService.organizationEdit911Provisioning({ Enabled: true, Cascade: true });
            if (state.url === views.E911_TABLE) return true;
            this.router.navigate([views.E911_TABLE]);
            return false;
          } else {
            if (state.url === views.E911_NO_BALANCE) return true;
            this.router.navigate([views.E911_NO_BALANCE]);
            return false;
          }
        }
      })
    );
  }
}
